import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { detailPageChipSx } from './detail-page-chip-sx';
import { usePageScroll } from '../../hooks/use-page-scroll/use-page-scroll';
import { useMuiSwitchableTheme } from '../../theme';

export interface DetailPageTab {
  key: string;
  title: string;
  element: ReactNode;
  visible?: boolean;
}

export interface DetailPageHeaderMeta {
  key: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  /**
   * Receives the click event so an action chip can anchor a popover to
   * itself — `event.currentTarget` is the chip.
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * When set, the chip shows a small `×` that calls this — for a fact the
   * user can take off the record (a tag, an assignee) without opening the
   * editor that put it there.
   */
  onDelete?: () => void;
  tooltip?: string;
  /**
   * Marks this chip as an ACTION rather than a fact about the record.
   *
   * Facts and actions otherwise render identically — same grey outline — so
   * an action sitting in the meta row reads as one more thing to be told
   * rather than something to click. An action chip takes the primary colour
   * and keeps its own icon on hover instead of swapping to the "opens
   * elsewhere" icon, which it doesn't.
   */
  action?: boolean;
  /**
   * Rendered in place of the standard chip — for a fact that already has a
   * component of its own (a job tag) and should look here exactly as it does
   * everywhere else. The other fields are then unused, bar `key`.
   */
  element?: ReactNode;
}

export interface DetailPageHeaderProps {
  /** Status label displayed in the header bar */
  status?: string;
  /** Subtitle displayed next to the status (e.g. an ID) */
  subtitle?: string;
  /**
   * A quieter line directly under the title — whose record this is (the
   * processor a job is booked with), where a chip would make it one fact
   * among several.
   */
  caption?: string;
  /** Metadata chips rendered alongside the status (customer, date, etc.) */
  meta?: DetailPageHeaderMeta[];
  /** Action buttons rendered on the right side of the header */
  actions?: ReactNode;
  /** Callback for the back button. If omitted, no back button is shown. */
  onBack?: () => void;
  /** Foreground color for the status. When `statusBgColor` is also set, the status is rendered as a chip. */
  statusColor?: string;
  /** Background color for the status chip. When set, status is rendered as a filled chip using `statusColor` for text. */
  statusBgColor?: string;
}

export interface DetailPageSidebarSection {
  key: string;
  element: ReactNode;
}

export interface DetailPageLayoutProps {
  /** Header bar configuration */
  header?: DetailPageHeaderProps;
  /** Custom header element — rendered instead of the default header bar */
  headerElement?: ReactNode;
  /**
   * Rendered under the header and above the tabs, so it stays on screen
   * whichever tab is selected.
   *
   * For the one thing the page most needs to say — "pay the deposit to
   * confirm this booking". Put in a sidebar section it becomes a tab of its
   * own on mobile, which is the one place it must not be: a producer reading
   * their animals would never see it.
   */
  banner?: ReactNode;
  /** Tabs rendered in the left column */
  tabs: DetailPageTab[];
  /** Currently active tab key */
  activeTab: string;
  /** Callback when a tab is selected */
  onTabChange: (tab: string) => void;
  /** Sidebar sections rendered in the right column, separated by dividers */
  sidebar?: DetailPageSidebarSection[];
  /** Footer element rendered at the bottom of the left column */
  footer?: ReactNode;
  /** Max width of the page content (default 1200) */
  maxWidth?: number;
  /** Width of the sidebar on desktop (default 300) */
  sidebarWidth?: number;
}

const DEFAULT_SIDEBAR_WIDTH = 300;

// A gentle fade + lift used to ease the page columns in on render. Uses
// `backwards` fill (not `both`) so no transform lingers after the animation —
// a leftover identity transform would otherwise interfere with the sticky
// header / sidebar inside these columns.
const pageEntranceSx = (delayMs: number) => ({
  '@keyframes detailPageEntrance': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'detailPageEntrance 0.35s ease backwards',
  animationDelay: `${delayMs}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * One sidebar section, with the rule that separates it from the one before.
 *
 * The rule is a border on the section rather than a `<Divider>` rendered
 * beside it, because a section's element can render nothing — an invoice card
 * before there is an invoice — and the layout can't know that from the outside.
 * A sibling divider was drawn regardless, leaving a rule with nothing under
 * it. As CSS, an empty section removes itself and its rule with it, and a
 * section only gets a rule when something non-empty precedes it.
 * @param inset - Space between the rule and the section's content.
 * @returns The section's `sx`.
 */
const sidebarSectionSx = (inset: number) => ({
  '&:empty': { display: 'none' },
  '&:not(:empty) ~ &:not(:empty)': {
    borderTop: 1,
    borderColor: 'divider',
    pt: inset,
  },
});

function MetaChip({ meta }: { meta: DetailPageHeaderMeta }) {
  const [hover, setHover] = useState(false);
  const interactive = !!meta.onClick || !!meta.href;
  const isAction = !!meta.action;
  const icon =
    interactive && hover && !isAction ? (
      <OpenInNewIcon />
    ) : (
      (meta.icon as ReactNode | undefined)
    );

  const chip = (
    <Chip
      label={meta.label}
      icon={icon as any}
      size="small"
      variant="outlined"
      onClick={meta.onClick}
      onDelete={meta.onDelete}
      clickable={interactive}
      component={meta.href ? 'a' : 'div'}
      href={meta.href}
      target={meta.href ? '_blank' : undefined}
      rel={meta.href ? 'noopener noreferrer' : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      sx={{
        ...detailPageChipSx,
        bgcolor: 'transparent',
        borderColor: isAction ? 'primary.main' : 'divider',
        color: isAction ? 'primary.main' : undefined,
        ...(isAction && {
          '&:hover': { bgcolor: 'action.hover' },
        }),
        '& .MuiChip-label': {
          ...detailPageChipSx['& .MuiChip-label'],
          ...(isAction && { fontWeight: 600 }),
        },
        '& .MuiChip-icon': {
          color: isAction ? 'primary.main' : 'text.secondary',
          fontSize: 13,
          ml: '6px',
          mr: '-4px',
          '& svg': { fontSize: 'inherit' },
        },
      }}
    />
  );

  return meta.tooltip ? <Tooltip title={meta.tooltip}>{chip}</Tooltip> : chip;
}

/**
 * The detail-page header bar: back arrow, title, a status chip and metadata
 * chips, and an optional action slot.
 *
 * Exported so surfaces that aren't a whole detail page can wear the same header
 * — the dashboard's resolve dialog reuses it so a job looks the same there as
 * it does on its own page.
 * @param root0
 * @param root0.status
 * @param root0.subtitle
 * @param root0.caption
 * @param root0.meta
 * @param root0.actions
 * @param root0.onBack
 * @param root0.statusColor
 * @param root0.statusBgColor
 */
export function DetailPageHeader({
  status,
  subtitle,
  caption,
  meta,
  actions,
  onBack,
  statusColor,
  statusBgColor,
}: DetailPageHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasMeta = !!meta && meta.length > 0;
  const statusAsChip = !!status && !!statusBgColor;

  const chipsRow =
    status || hasMeta ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: isMobile ? 0 : 0.5 }}
      >
        {status &&
          (statusAsChip ? (
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor: statusBgColor,
                color: statusColor,
                fontWeight: 500,
                height: 22,
                borderRadius: 999,
                '& .MuiChip-label': { px: 1.25, fontSize: '0.75rem' },
              }}
            />
          ) : (
            <Typography
              variant="subtitle2"
              sx={{ color: statusColor ?? 'text.secondary' }}
            >
              {status}
            </Typography>
          ))}
        {hasMeta &&
          meta!.map((m) =>
            m.element ? (
              <React.Fragment key={m.key}>{m.element}</React.Fragment>
            ) : (
              <MetaChip key={m.key} meta={m} />
            ),
          )}
      </Stack>
    ) : null;

  // On a phone the chips get their own full-width row under the header rather
  // than living in `subheader`, which is inside CardHeader's content column and
  // so is squeezed into whatever the action button leaves over — the status and
  // meta chips ended up stacked in a narrow gutter beside it.
  const header = (
    <CardHeader
      avatar={
        onBack && (
          <Tooltip title="Back">
            <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )
      }
      title={
        caption ? (
          <>
            {subtitle}
            {/* A block-level span: CardHeader wraps the title in one. */}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {caption}
            </Typography>
          </>
        ) : (
          subtitle
        )
      }
      subheader={isMobile ? undefined : chipsRow}
      action={actions}
      slotProps={{
        title: { variant: 'h6' },
        // Top-aligned on mobile so Back and the action button line up with the
        // title rather than floating against the middle of a two-line header.
        action: { sx: { alignSelf: isMobile ? 'flex-start' : 'center', m: 0 } },
        avatar: { sx: { alignSelf: isMobile ? 'flex-start' : 'center' } },
      }}
      sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 1, md: 1.5 },
        bgcolor: 'action.hover',
        alignItems: isMobile ? 'flex-start' : 'center',
        // The border moves to the chips row on mobile, since that becomes the
        // bottom of the header block.
        borderBottom: isMobile ? 0 : 1,
        borderColor: 'divider',
        borderTopLeftRadius: { xs: 0, md: '10px' },
        borderTopRightRadius: { xs: 0, md: '10px' },
        gap: { xs: 1, md: 1.5 },
      }}
    />
  );

  if (!isMobile || !chipsRow) return header;

  return (
    <>
      {header}
      <Box
        sx={{
          px: 1.5,
          // A little air between the title row and the chips; flush against
          // it they read as part of the title.
          pt: 0.75,
          pb: 1,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {chipsRow}
      </Box>
    </>
  );
}

const SIDEBAR_TAB_KEY = '__details';

export interface DetailPageLayoutSkeletonProps {
  /** Show a sidebar skeleton (default true) */
  sidebar?: boolean;
  /** Number of tab placeholders (default 3) */
  tabs?: number;
  /** Max width of the page content (default 1200) */
  maxWidth?: number;
  /** Width of the sidebar on desktop (default 300) */
  sidebarWidth?: number;
}

/**
 * Loading state for a `DetailPageLayout`, laid out like the real thing: the
 * tinted header bar (back, title, chips, action), the tab strip, a few blocks
 * of content, and the sidebar. Sized by the same rules as the layout so the
 * page doesn't jump when the record arrives.
 * @param root0 - Props.
 * @param root0.sidebar - Whether to draw the sidebar.
 * @param root0.tabs - Number of tab placeholders; one or fewer hides the strip.
 * @param root0.maxWidth - Max width of the page content.
 * @param root0.sidebarWidth - Width of the sidebar on desktop.
 * @returns The skeleton page.
 */
export function DetailPageLayoutSkeleton({
  sidebar: showSidebar = true,
  tabs: tabCount = 3,
  maxWidth = 1200,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
}: DetailPageLayoutSkeletonProps = {}) {
  const theme = useTheme();

  // A titled block of label/value pairs, the shape most detail tabs open on.
  const fieldBlock = (fieldCount: number) => (
    <Box>
      <Skeleton variant="text" width={140} height={26} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          mt: 1,
        }}
      >
        {Array.from({ length: fieldCount }).map((_, i) => (
          <Box key={i}>
            <Skeleton variant="text" width="35%" height={16} />
            <Skeleton variant="text" width="70%" height={24} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const sidebarBlock = (lineWidths: string[]) => (
    <Box>
      <Skeleton variant="text" width="45%" height={22} />
      {lineWidths.map((width) => (
        <Skeleton
          key={width}
          variant="text"
          width={width}
          height={20}
          sx={{ mt: 1 }}
        />
      ))}
    </Box>
  );

  return (
    <Box
      aria-busy
      sx={{
        bgcolor: 'background.default',
        // Same rule as the layout — see the note there on why not `100vh`.
        minHeight: 'calc(100dvh - var(--app-header-height, 0px))',
      }}
    >
      {/* Full bleed on a phone: the page IS the detail record, so the gutter
          and the rounded card around it were spending width and vertical space
          framing something that has nothing beside it. */}
      <Box
        sx={{
          px: { xs: 0, md: 3 },
          py: { xs: 0, md: 2 },
          maxWidth,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          {/* Left column */}
          <Box
            sx={{
              // Desktop only — see the note on the real column below.
              flex: { xs: 'none', md: 1 },
              minWidth: 0,
              bgcolor: 'background.paper',
              borderRadius: { xs: 0, md: '10px' },
              height: {
                xs: 'calc(100dvh - var(--app-header-height, 0px))',
                md: `calc(100dvh - ${theme.spacing(5)})`,
              },
              overflow: 'hidden',
            }}
          >
            {/* Header bar: back, title + chips, action */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.5,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
                borderTopLeftRadius: { xs: 0, md: '10px' },
                borderTopRightRadius: { xs: 0, md: '10px' },
              }}
            >
              <Skeleton variant="circular" width={28} height={28} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="40%" height={30} />
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Skeleton variant="rounded" width={72} height={22} />
                  <Skeleton variant="rounded" width={96} height={22} />
                  <Skeleton variant="rounded" width={84} height={22} />
                </Stack>
              </Box>
              <Skeleton variant="rounded" width={96} height={36} />
            </Stack>

            {/* Tab strip */}
            {tabCount > 1 && (
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                sx={{
                  height: 36,
                  px: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {Array.from({ length: tabCount }).map((_, i) => (
                  <Skeleton key={i} variant="text" width={70} height={22} />
                ))}
              </Stack>
            )}

            {/* Content */}
            <Stack spacing={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              {fieldBlock(4)}
              <Skeleton variant="rounded" width="100%" height={140} />
              {fieldBlock(2)}
              <Skeleton variant="rounded" width="100%" height={100} />
            </Stack>
          </Box>

          {/* Sidebar — desktop only; on a phone it becomes a tab. */}
          {showSidebar && (
            <Card
              elevation={0}
              sx={{
                display: { xs: 'none', md: 'block' },
                width: { md: sidebarWidth },
                flexShrink: 0,
                alignSelf: 'flex-start',
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  '&:last-child': { pb: 2 },
                }}
              >
                {sidebarBlock(['75%', '60%', '80%'])}
                <Divider />
                {sidebarBlock(['70%', '55%'])}
                <Divider />
                {sidebarBlock(['65%', '85%', '50%'])}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function DetailPageLayout({
  header,
  headerElement,
  banner,
  tabs,
  activeTab,
  onTabChange,
  sidebar,
  footer,
  maxWidth = 1200,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
}: DetailPageLayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // On a phone the DOCUMENT scrolls rather than the column: it is the only
  // kind of scroll that makes the browser tuck its own toolbar away, which on
  // a phone in landscape is a sixth of the screen. The header and tabs then
  // pin to the screen, under the app header, instead of to the column. Not
  // inside a host that scrolls its own panel, where the document doesn't move.
  const { isPhoneLandscape } = useMuiSwitchableTheme();
  const ref = useRef<HTMLDivElement>(null);
  const pageScroll = usePageScroll(ref);

  // Measured column height.
  //
  // The fallback below is `100dvh - 40px`, which assumes the page sits in
  // about 40px of chrome. It doesn't always: the producer shell spends 24px
  // of outer padding plus its own top and bottom on the content area, so the
  // column overhung by that difference and the whole page scrolled to reveal
  // a column that was itself scrollable. Measuring the space actually below
  // the column's top edge removes the guess — same approach as SearchPage's
  // `autoFitHeight`.
  const [fittedHeight, setFittedHeight] = useState<string | null>(null);
  useEffect(() => {
    if (isMobile) {
      setFittedHeight(null);
      return undefined;
    }

    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // 16px of breathing room at the bottom, and a floor so a mis-measure
      // during an entrance animation can't collapse the column.
      setFittedHeight(`${Math.max(320, window.innerHeight - top - 16)}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    // The column can move without the window resizing — the shell's nav
    // opening, or a banner appearing above it.
    const observer = new ResizeObserver(measure);
    if (ref.current?.parentElement) observer.observe(ref.current.parentElement);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [isMobile]);

  // Height of the page header, which the tabs pin beneath on a phone.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || !isMobile) return undefined;
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  // Height of the tab strip, which a tab's own pinned rows sit beneath.
  // Measured like the header: its tabs' height is the theme's to change.
  // Held in state, not a ref: the strip mounts and unmounts with the tab
  // count, and the measurement has to follow it.
  const [tabStripEl, setTabStripEl] = useState<HTMLDivElement | null>(null);
  const [tabStripHeight, setTabStripHeight] = useState(0);
  useLayoutEffect(() => {
    if (!tabStripEl || !isMobile) {
      setTabStripHeight(0);
      return undefined;
    }
    const measure = () =>
      setTabStripHeight(tabStripEl.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(tabStripEl);
    return () => observer.disconnect();
  }, [tabStripEl, isMobile]);

  // Where the pinned header sits: the top of the column, or under the app
  // header when it is the document that scrolls.
  const stickyTop = pageScroll ? 'var(--app-header-height, 0px)' : 0;
  // In landscape the screen is ~400px tall and the pinned header was a
  // quarter of it, so there only the tabs pin and the header scrolls away.
  const pinnedHeaderHeight = isPhoneLandscape ? 0 : headerHeight;

  const hasSidebar = sidebar && sidebar.length > 0;

  // On mobile, track whether the injected "Details" tab is active via local state
  const [showDetailsTab, setShowDetailsTab] = useState(false);

  // Reset details tab when switching away from mobile
  useEffect(() => {
    if (!isMobile) setShowDetailsTab(false);
  }, [isMobile]);

  // On mobile, prepend a "Details" tab containing the sidebar content
  const allTabs = useMemo(() => {
    if (!isMobile || !hasSidebar) return tabs;
    const sidebarSections = (
      // `px: 1` on top of the tab area's own 8px, for 16 a side. The other
      // tabs hold cards and tables that bring their own inset; these
      // sections are bare label/value rows, and at 8px they read as
      // touching the screen edge.
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 1 }}>
        {sidebar.map((section) => (
          <Box key={section.key} sx={sidebarSectionSx(3)}>
            {section.element}
          </Box>
        ))}
      </Box>
    );
    // A page with its own Details tab gets the sidebar at the foot of that
    // tab, rather than a second tab with the same name beside it.
    const ownDetails = tabs.findIndex(
      (t) => t.key === 'details' || t.title === 'Details',
    );
    if (ownDetails !== -1) {
      return tabs.map((t, i) =>
        i === ownDetails
          ? {
              ...t,
              element: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {t.element}
                  {sidebarSections}
                </Box>
              ),
            }
          : t,
      );
    }
    const detailsTab: DetailPageTab = {
      key: SIDEBAR_TAB_KEY,
      title: 'Details',
      element: sidebarSections,
    };
    return [detailsTab, ...tabs];
  }, [isMobile, hasSidebar, sidebar, tabs]);

  const resolvedTab = showDetailsTab && isMobile ? SIDEBAR_TAB_KEY : activeTab;

  const visibleTabs = allTabs.filter((t) => t.visible !== false);

  const handleTabChange = (newTab: string) => {
    if (newTab === SIDEBAR_TAB_KEY) {
      setShowDetailsTab(true);
    } else {
      setShowDetailsTab(false);
      onTabChange(newTab);
    }
  };

  // A new tab starts at its top. Keyed on the tab that is actually showing,
  // not on the click, so it runs once the new content is in the DOM — a
  // smooth scroll started in the click handler was dropped when the content
  // swapped under it, leaving the new tab at the old tab's offset. A layout
  // effect, so the reset lands before the new tab is painted.
  useLayoutEffect(() => {
    if (pageScroll) window.scrollTo({ top: 0 });
    else if (ref.current) ref.current.scrollTop = 0;
  }, [resolvedTab, pageScroll]);

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        // NOT `100vh`. The app header is a `position: sticky` AppBar, so it
        // takes 64px of normal flow — asking for a full viewport HERE makes the
        // document 64px taller than the screen. The body then scrolls by that
        // much and carries this whole card up with it, taking the card's own
        // sticky header out of view: the header only stays put if nothing
        // outside its scroll container can scroll.
        //
        // The var is 0 when the header isn't a top bar (desktop left nav).
        //
        // Mobile only. On desktop the column below sets its own MEASURED
        // height, which already accounts for whatever the surrounding shell
        // spends above it — forcing a full viewport here on top of that made
        // the region taller than the space it sits in, so the document
        // scrolled by exactly the shell's padding and the page had two
        // scrollbars.
        minHeight: {
          xs: 'calc(100dvh - var(--app-header-height, 0px))',
          md: 'auto',
        },
      }}
    >
      {/* Full bleed on a phone: the page IS the detail record, so the gutter
          and the rounded card around it were spending width and vertical space
          framing something that has nothing beside it. */}
      <Box
        sx={{
          px: { xs: 0, md: 3 },
          py: { xs: 0, md: 2 },
          maxWidth,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          {/* Left column: header + tabs + content */}
          <Box
            ref={ref}
            sx={{
              // `flex: 1` only on desktop, where the parent is a ROW and it
              // shares the width with the sidebar. On a phone the parent is a
              // column, so the same shorthand sets a zero flex-basis on the
              // HEIGHT axis — which outranks the `height` below, and with no
              // definite parent height resolves to the content's height. The
              // column then never scrolls, whatever is outside it does, and
              // the sticky header rides away with the page.
              flex: { xs: 'none', md: 1 },
              minWidth: 0,
              bgcolor: 'background.paper',
              borderRadius: { xs: 0, md: '10px' },
              // Desktop subtracts the page's own padding. Mobile has none, so
              // it fills the viewport below the app header instead — the var
              // the header publishes for exactly this (see theme.tsx).
              ...(pageScroll
                ? {
                    // As tall as its content, and at least the screen. Not
                    // clipped: a clipping ancestor is what the header and
                    // tabs would stick to, instead of the screen.
                    minHeight: 'calc(100dvh - var(--app-header-height, 0px))',
                    overflow: 'visible',
                  }
                : {
                    height: {
                      xs: 'calc(100dvh - var(--app-header-height, 0px))',
                      md: fittedHeight ?? `calc(100dvh - ${theme.spacing(5)})`,
                    },
                    overflow: 'scroll',
                  }),
              // The fade belongs to a column that scrolls; over a whole page
              // it would dim the last tenth of the record.
              WebkitMask:
                footer && !pageScroll
                  ? 'linear-gradient(180deg, #0002, #000 0% 90%, #0002)'
                  : 'unset',
              ...pageEntranceSx(0),
            }}
          >
            {/* Sticky header + tabs.
                Desktop: one block, pinned whole — header, banner and tabs.
                Phone: the banner would hold a third of the screen for as long
                as the page is open, so there only the header and the tabs
                pin, and the banner between them scrolls away. `contents`
                dissolves this wrapper so the two can stick to the column
                itself rather than to a box that ends where the tabs do. */}
            <Box
              sx={{
                display: { xs: 'contents', md: 'block' },
                position: { md: 'sticky' },
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper',
                borderTopLeftRadius: { xs: 0, md: '10px' },
                borderTopRightRadius: { xs: 0, md: '10px' },
              }}
            >
              {/* Header */}
              <Box
                ref={headerRef}
                sx={{
                  position: {
                    xs: isPhoneLandscape ? 'static' : 'sticky',
                    md: 'static',
                  },
                  top: stickyTop,
                  zIndex: 10,
                  bgcolor: 'background.paper',
                }}
              >
                {headerElement ?? (header && <DetailPageHeader {...header} />)}
              </Box>

              {/* Banner — above the tabs, so it survives a tab change. */}
              {banner && <Box sx={{ mb: { xs: 1, md: 2 } }}>{banner}</Box>}

              {/* Tabs (hidden when only one visible tab) */}
              {visibleTabs.length > 1 && (
                <TabContext value={resolvedTab}>
                  <Box
                    ref={setTabStripEl}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      position: { xs: 'sticky', md: 'static' },
                      // Directly under the pinned header.
                      top: pageScroll
                        ? `calc(${stickyTop} + ${pinnedHeaderHeight}px)`
                        : headerHeight,
                      zIndex: 9,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <TabList
                      onChange={(_, val: string) => handleTabChange(val)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: 36,
                        '& .MuiTab-root': {
                          minHeight: 36,
                          py: 0.5,
                          px: 2,
                          fontSize: '0.8125rem',
                        },
                      }}
                    >
                      {visibleTabs.map((t) => (
                        <Tab key={t.key} label={t.title} value={t.key} />
                      ))}
                    </TabList>
                  </Box>
                </TabContext>
              )}
            </Box>

            {/* Tab content */}
            <Box
              sx={{
                p: 1,
                pt: 0,
                minHeight: '50dvh',
                // Tells anything in a tab that pins itself (a SearchPage's
                // search bar) where the pinned header and tabs end.
                ...(pageScroll && {
                  '--page-sticky-top': `calc(${stickyTop} + ${
                    pinnedHeaderHeight + tabStripHeight
                  }px)`,
                }),
              }}
            >
              <TabContext value={resolvedTab}>
                {allTabs.map((t) => (
                  <TabPanel
                    key={t.key}
                    value={t.key}
                    sx={{ px: 0, pt: 1, pb: 0 }}
                  >
                    {t.element}
                  </TabPanel>
                ))}
              </TabContext>
            </Box>

            {/* Footer */}
            {footer && (
              <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pb: 5 }}>
                <Divider sx={{ mb: 2 }} />
                {footer}
              </Box>
            )}
          </Box>

          {/* Right column: sidebar (hidden on mobile — shown as tab instead) */}
          {hasSidebar && (
            <Card
              elevation={0}
              sx={{
                display: { xs: 'none', md: 'block' },
                width: { md: sidebarWidth },
                flexShrink: 0,
                alignSelf: 'flex-start',
                position: { md: 'sticky' },
                top: { md: 16 },
                // Capped to the same measured height as the content column
                // and scrolled internally. Sized to its content it could be
                // taller than the viewport, and then the PAGE scrolled —
                // which is the one thing this layout is meant to avoid.
                ...(fittedHeight
                  ? { maxHeight: fittedHeight, overflowY: 'auto' }
                  : {}),
                ...pageEntranceSx(90),
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  '&:last-child': { pb: 1 },
                }}
              >
                {sidebar.map((section) => (
                  <Box key={section.key} sx={sidebarSectionSx(2)}>
                    {section.element}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
}
